import React from 'react';
import { useSearchParams } from 'react-router-dom';

const QUERY_PAGE_KEY = 'page';
const QUERY_LIMIT_KEY = 'limit';

interface PaginationOptions {
  startPage?: number;
  startLimit?: number;
}

export const usePagination = (opts: PaginationOptions = {}) => {
  const { startPage, startLimit } = opts;

  const [search, setSearch] = useSearchParams();
  const decoded = new URLSearchParams(search);

  const initialPage = React.useMemo(
    () =>
      decoded.has(QUERY_PAGE_KEY)
        ? !isNaN(Number(decoded.get(QUERY_PAGE_KEY)))
          ? Number(decoded.get(QUERY_PAGE_KEY))
          : 1
        : startPage ?? 1,
    [search],
  );

  const initialLimit = React.useMemo(
    () =>
      decoded.has(QUERY_LIMIT_KEY)
        ? !isNaN(Number(decoded.get(QUERY_LIMIT_KEY)))
          ? Number(decoded.get(QUERY_LIMIT_KEY))
          : 10
        : startLimit ?? 10,
    [search],
  );

  const [page, setPageBase] = React.useState<number>(initialPage);
  const [limit, setLimitBase] = React.useState<number>(initialLimit);

  const setPage = React.useCallback(
    (nextPage: number) => {
      if (page === nextPage) return;

      setPageBase(nextPage);

      setSearch((prev) => {
        prev.set(QUERY_PAGE_KEY, nextPage.toString());
        return prev;
      });
    },
    [page, setPageBase],
  );

  const setLimit = React.useCallback(
    (nextLimit: number) => {
      if (limit === nextLimit) return;

      setLimitBase(nextLimit);

      setSearch((prev) => {
        prev.set(QUERY_LIMIT_KEY, nextLimit.toString());
        return prev;
      });
    },
    [limit, setLimitBase],
  );

  return { page, setPage, limit, setLimit };
};
